// Cloudflare Realtime Service - 管理音视频通话功能
import ApiService from './api';

class RealtimeService {
    constructor() {
        this.currentSession = null;
        this.authToken = null;
        this.sessionHandlers = new Set();
        this.isInitialized = false;
    }

    /**
     * 初始化 Realtime 会话
     * @param {string} chatId - 聊天ID
     * @param {string} clientId - 客户端ID
     * @param {string} appId - 应用ID
     * @returns {Promise<string>} 返回 authToken
     */
    async initSession(chatId, clientId, appId) {
        try {
            console.log('Initializing Realtime session:', { chatId, clientId, appId });

            // 从后端获取 authToken
            const response = await ApiService.generateRealtimeToken(chatId, clientId, appId);

            if (response.code === 0 && response.data && response.data.authToken) {
                this.authToken = response.data.authToken;
                this.currentSession = {
                    chatId,
                    clientId,
                    appId,
                    authToken: this.authToken,
                    sessionId: response.data.sessionId,
                    createdAt: new Date()
                };

                this.isInitialized = true;
                this.notifySessionHandlers('initialized', this.currentSession);

                console.log('Realtime session initialized successfully');
                return this.authToken;
            } else {
                throw new Error(response.msg || 'Failed to get auth token');
            }
        } catch (error) {
            console.error('Failed to initialize Realtime session:', error);
            throw error;
        }
    }

    /**
     * 开始音频通话
     * @param {string} chatId - 聊天ID
     * @param {string} clientId - 客户端ID
     * @param {string} appId - 应用ID
     * @returns {Promise<string>} 返回 authToken
     */
    async startAudioCall(chatId, clientId, appId) {
        try {
            if (!this.isInitialized || !this.authToken) {
                await this.initSession(chatId, clientId, appId);
            }

            this.notifySessionHandlers('audio_call_started', {
                type: 'audio',
                authToken: this.authToken
            });

            return this.authToken;
        } catch (error) {
            console.error('Failed to start audio call:', error);
            throw error;
        }
    }

    /**
     * 开始视频通话
     * @param {string} chatId - 聊天ID
     * @param {string} clientId - 客户端ID
     * @param {string} appId - 应用ID
     * @returns {Promise<string>} 返回 authToken
     */
    async startVideoCall(chatId, clientId, appId) {
        try {
            if (!this.isInitialized || !this.authToken) {
                await this.initSession(chatId, clientId, appId);
            }

            this.notifySessionHandlers('video_call_started', {
                type: 'video',
                authToken: this.authToken
            });

            return this.authToken;
        } catch (error) {
            console.error('Failed to start video call:', error);
            throw error;
        }
    }

    /**
     * 开始屏幕共享
     * @param {string} chatId - 聊天ID
     * @param {string} clientId - 客户端ID
     * @param {string} appId - 应用ID
     * @returns {Promise<string>} 返回 authToken
     */
    async startScreenShare(chatId, clientId, appId) {
        try {
            if (!this.isInitialized || !this.authToken) {
                await this.initSession(chatId, clientId, appId);
            }

            this.notifySessionHandlers('screen_share_started', {
                type: 'screen',
                authToken: this.authToken
            });

            return this.authToken;
        } catch (error) {
            console.error('Failed to start screen share:', error);
            throw error;
        }
    }

    /**
     * 结束当前会话
     */
    async endSession() {
        try {
            if (this.currentSession) {
                // 通知后端结束会话
                await ApiService.endRealtimeSession(
                    this.currentSession.chatId,
                    this.currentSession.sessionId
                );

                this.notifySessionHandlers('session_ended', this.currentSession);

                this.currentSession = null;
                this.authToken = null;
                this.isInitialized = false;
            }
        } catch (error) {
            console.error('Failed to end Realtime session:', error);
        }
    }

    /**
     * 获取当前会话信息
     * @returns {Object|null} 当前会话信息
     */
    getCurrentSession() {
        return this.currentSession;
    }

    /**
     * 获取当前 authToken
     * @returns {string|null} authToken
     */
    getAuthToken() {
        return this.authToken;
    }

    /**
     * 注册会话事件处理器
     * @param {Function} handler - 事件处理函数
     */
    onSessionChange(handler) {
        this.sessionHandlers.add(handler);
    }

    /**
     * 移除会话事件处理器
     * @param {Function} handler - 事件处理函数
     */
    offSessionChange(handler) {
        this.sessionHandlers.delete(handler);
    }

    /**
     * 通知所有会话处理器
     * @param {string} event - 事件类型
     * @param {Object} data - 事件数据
     */
    notifySessionHandlers(event, data) {
        this.sessionHandlers.forEach(handler => {
            try {
                handler(event, data);
            } catch (error) {
                console.error('Error in session handler:', error);
            }
        });
    }

    /**
     * 清理资源
     */
    destroy() {
        this.endSession();
        this.sessionHandlers.clear();
    }
}

// 导出单例
export default new RealtimeService();
